import React, { Fragment, useEffect, useRef, useState } from 'react';

import {
  NavigateDown,
  NavigateUp,
  Play,
} from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import { ServerErrorRed } from '@randstad-lean-mobile-factory/react-assets/dist/illustrations';
import {
  Button,
  Link,
  Loader,
  Modal,
  ModalActions,
  ModalContent,
  StyledTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@randstad-lean-mobile-factory/react-components-core';
import moment from 'moment';

import { useFetchCronJobDetails } from '../../../Hooks/CronJobs/useFetchCronJobDetails';
import { useRunCronJobManually } from '../../../Hooks/CronJobs/useRunCronJobManually';

import styles from './CronJobDetailsModal.module.scss';
import { Props } from './CronJobDetailsModal.types';

const CronJobDetailsModalContent = ({ onClose, cronJob }: Props) => {
  const { data: cronJobDetails, isLoading } = useFetchCronJobDetails(cronJob.cronJobId);

  const runCronJobManually = useRunCronJobManually(cronJob.cronJobId, {
    onSuccess: executionId => {
      setSelectedExecution(executionId);
    },
    onSettled: () => {
      setTimeout(() => runCronJobManually.reset(), 500);
    },
  });

  const [selectedExecution, setSelectedExecution] = useState<string>();
  const [selectedTrace, setSelectedTrace] = useState<number>();

  const ref = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    if (selectedExecution) {
      ref.current
        ?.querySelector(`[data-execution-id="${selectedExecution}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedExecution]);
  return (
    <>
      {isLoading ? (
        <div className={styles.illustration}>
          <Loader size="large" />
          chargement des tâches
        </div>
      ) : !cronJobDetails ? (
        <div className={styles.illustration}>
          <ServerErrorRed />
          désolé, une erreur s'est produite
        </div>
      ) : (
        <>
          <ModalContent title={`Dernières exécutions de ${cronJob.cronJobId}`}>
            <div>
              <div>
                <Button
                  size="medium"
                  onClick={() => runCronJobManually.mutate()}
                  mutationStatus={
                    cronJobDetails?.currentExecutionId ? 'loading' : runCronJobManually.status
                  }
                  disabled={!cronJobDetails}
                >
                  <Play variant="fill" /> lancer manuellement
                </Button>
              </div>
              <StyledTable size="small" className={styles.executionTable}>
                <TableHeader>
                  <TableRow>
                    <TableHead>date</TableHead>
                    <TableHead>mode d'exécution</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody ref={ref}>
                  {cronJobDetails.executions.map(execution => (
                    <Fragment key={execution.executionId}>
                      <TableRow
                        isSelected={selectedExecution === execution.executionId}
                        onClick={() => {
                          if (selectedExecution === execution.executionId)
                            setSelectedExecution(undefined);
                          else {
                            setSelectedExecution(execution.executionId);
                            setSelectedTrace(undefined);
                          }
                        }}
                        isClickable
                        data-execution-id={execution.executionId}
                      >
                        <TableCell>{moment(execution.createdAt).format('LLLL')}</TableCell>
                        <TableCell>{execution.mode}</TableCell>
                        <TableCell>
                          {selectedExecution === execution.executionId ? (
                            <NavigateUp color="blue" size="medium" />
                          ) : (
                            <NavigateDown color="blue" size="medium" />
                          )}
                        </TableCell>
                      </TableRow>
                      {selectedExecution === execution.executionId && (
                        <tr>
                          <td colSpan={3}>
                            <StyledTable color="white" size="small" className={styles.logTable}>
                              <TableHeader>
                                <TableRow color="white">
                                  <TableHead>level</TableHead>
                                  <TableHead>message</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {execution.logs.map((log, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{log.level}</TableCell>
                                    <TableCell tdClassName={styles.log}>
                                      {typeof log.message === 'string'
                                        ? log.message
                                        : JSON.stringify(log.message, undefined, 2)}
                                      {log.trace &&
                                        (selectedTrace === idx ? (
                                          <>
                                            <Link
                                              size="xsmall"
                                              onClick={() => setSelectedTrace(undefined)}
                                            >
                                              voir moins
                                            </Link>
                                            {log.trace}
                                          </>
                                        ) : (
                                          <Link size="xsmall" onClick={() => setSelectedTrace(idx)}>
                                            voir plus
                                          </Link>
                                        ))}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </StyledTable>
                            {cronJobDetails.currentExecutionId === execution.executionId && (
                              <div className={styles.executionLoader}>
                                <Loader size="xsmall" />
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </StyledTable>
            </div>
          </ModalContent>
          <ModalActions side="right">
            <Button variant="primary" onClick={() => onClose?.()}>
              quitter
            </Button>
          </ModalActions>
        </>
      )}
    </>
  );
};

export const CronJobDetailsModalComponent = ({ cronJob, onClose, open }: Props) => {
  return (
    <Modal size="medium" open={open} onClose={onClose}>
      <CronJobDetailsModalContent open={open} onClose={onClose} cronJob={cronJob} />
    </Modal>
  );
};
